// Microsoft Teams JS SDK wrapper.
//
// The SDK is loaded only when the SPA actually runs inside a Teams
// tab; tests + Playwright use the optional `window.__KWANZA_TEAMS_STUB__`
// escape hatch. The stub presence is intentionally narrow — production
// code never sets it, and the contract is the same shape as
// `TeamsTabContext` + a `token` field — so the surface here stays one
// branch wide.
//
// Auth flow (per F37 spec §5.6):
//   1. `microsoftTeams.app.initialize()`
//   2. `microsoftTeams.app.getContext()` → reads tenantId + locale
//   3. `microsoftTeams.authentication.getAuthToken({})` → AAD ID token
//
// The token is sent on every API call as `Authorization: Bearer ...`.
// On 401 → re-acquire + retry once; on second 401 the SPA surfaces a
// "session expired" prompt with a re-init button.

import { app, authentication } from "@microsoft/teams-js";

import { TeamsAuthError } from "./api";

/** Slim shape of the data we actually need from `app.getContext()`. */
export interface TeamsTabContext {
  tenantId: string;
  userPrincipalName: string | null;
  locale: string | null;
  /** Display name of the channel hosting the tab (e.g. "Contabilidade"),
   * or null for a personal/static tab with no channel context. Feeds the
   * status-strip scope line; a Variables-layer value from the live Teams
   * host, never baked into the bundle. */
  channelName: string | null;
}

/** Shape the Playwright fixture (or any test driver) injects via
 * `window.__KWANZA_TEAMS_STUB__` before the SPA boots. */
export interface TeamsStub {
  tenantId: string;
  locale?: string | null;
  upn?: string | null;
  /** Optional channel display name; threads into the status-strip scope
   * line in tests / Playwright. */
  channelName?: string | null;
  /** Host theme the stub reports (`default` | `dark` | `contrast`), as the
   * live Teams context would. Absent → the SPA falls back to the OS
   * `prefers-color-scheme`. */
  theme?: string | null;
  /** Static token issued from `getAuthToken()` when set. */
  token?: string | null;
  /** When true, `getAuthToken()` rejects (simulates Teams SSO failure). */
  failGetToken?: boolean;
  /** Test instrumentation: count of `getAuthToken()` calls. */
  tokenIssueCount?: number;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    __KWANZA_TEAMS_STUB__?: TeamsStub;
  }
}

function _stub(): TeamsStub | undefined {
  if (typeof window === "undefined") return undefined;
  return window.__KWANZA_TEAMS_STUB__;
}

let _initialized = false;
let _initPromise: Promise<void> | null = null;

/** Idempotent SDK initialize — Teams throws on double-init. */
export async function initializeTeams(): Promise<void> {
  if (_stub()) {
    _initialized = true;
    return;
  }
  if (_initialized) return;
  if (_initPromise) {
    await _initPromise;
    return;
  }
  _initPromise = app.initialize();
  try {
    await _initPromise;
    _initialized = true;
  } finally {
    _initPromise = null;
  }
}

/** Read the current Teams tab context. */
export async function getContext(): Promise<TeamsTabContext> {
  await initializeTeams();
  const stub = _stub();
  if (stub) {
    return {
      tenantId: stub.tenantId,
      userPrincipalName: stub.upn ?? null,
      locale: stub.locale ?? null,
      channelName: stub.channelName ?? null,
    };
  }
  const ctx = await app.getContext();
  return {
    tenantId: ctx.user?.tenant?.id ?? "",
    userPrincipalName: ctx.user?.userPrincipalName ?? null,
    locale: ctx.app?.locale ?? null,
    channelName: ctx.channel?.displayName ?? null,
  };
}

/** Read the host's current theme (`default` | `dark` | `contrast`) and
 * subscribe to changes. Returns `null` when there is no Teams host to ask
 * (local dev, Playwright without a `theme` on the stub) so the caller can
 * fall back to the OS `prefers-color-scheme`.
 *
 * The theme is a USER setting read at runtime — the same class of value as
 * `tenantId` / `locale` / `channelName` the context already carries — never
 * a per-tenant variable baked into the bundle. */
export async function getThemeAndSubscribe(
  onChange: (theme: string) => void,
): Promise<string | null> {
  const stub = _stub();
  if (stub) {
    // The stub is a static fixture: no host to push changes from.
    return stub.theme ?? null;
  }
  await initializeTeams();
  const ctx = await app.getContext();
  app.registerOnThemeChangeHandler(onChange);
  return ctx.app?.theme ?? null;
}

/** Acquire a fresh AAD ID token from the Teams host. The host
 * decides whether to surface a consent prompt or return a cached
 * token; we don't decide. */
export async function getAuthToken(): Promise<string> {
  await initializeTeams();
  const stub = _stub();
  if (stub) {
    stub.tokenIssueCount = (stub.tokenIssueCount ?? 0) + 1;
    if (stub.failGetToken) {
      throw new TeamsAuthError("stub: getAuthToken() rejected");
    }
    return stub.token ?? "STUB-TOKEN";
  }
  try {
    return await authentication.getAuthToken({});
  } catch (err) {
    // Wrap the Teams SDK's rejection so the dashboard can surface the
    // real reason (e.g. resourceDisabled / consentRequired /
    // appNotInstalled) on-screen. This fires BEFORE any backend call —
    // the SSO token never gets minted — so it's the failure mode behind
    // the 2026-05-29 first-load "fetch_failed" panel with no backend
    // request in the logs.
    const reason = err instanceof Error ? err.message : String(err ?? "unknown");
    throw new TeamsAuthError(reason);
  }
}

/** Test-only reset of the module-level init guard. Production code
 * never calls this — the SDK is initialized once per SPA session. */
export function _resetForTest(): void {
  _initialized = false;
  _initPromise = null;
}
